import { agencyIdFromPath, assignedAgencies } from '../../features/auth/agencyAccess';
import { useNavigate } from '@tanstack/react-router';
import { LogOut } from 'lucide-react';
import { ThemeMode } from '@shigo-ui/theme';

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shigo-ui/ui';

import {
  canOpenMainDashboard,
  canOpenPublisherDashboard,
  publisherDashboardPath,
  publisherIdFromPath,
} from '../../features/auth/dashboardDestination';
import { ShigoRole } from '../../features/auth/permissions';
import { useAssignedPublishersQuery } from '../../features/dashboards/hooks';
import { dashboardT } from '../../lib/messages';
import { useShigoSession } from '../../lib/useShigoSession';
import { useShigoTheme } from '../../lib/useShigoTheme';
import { ShigoAccountDashboardSwitcher } from './ShigoAccountDashboardSwitcher';
import { accountDisplayName, accountInitials } from './shigoAccountMenuModel';

type ShigoAccountMenuProps = {
  pathname: string;
};

export function ShigoAccountMenu({ pathname }: ShigoAccountMenuProps) {
  const navigate = useNavigate();
  const { session } = useShigoSession();
  const { themeMode, setThemeMode } = useShigoTheme();
  const isSuperAdmin = session?.user.roles.includes(ShigoRole.SuperAdmin) ?? false;
  const canOpenMain = canOpenMainDashboard(session?.user);
  const canOpenPublishers = canOpenPublisherDashboard(session?.user);
  const publisherLandingPath = publisherDashboardPath(session?.user.permissions ?? []);
  const publishersQuery = useAssignedPublishersQuery(!isSuperAdmin && canOpenPublishers);
  const agencyId = agencyIdFromPath(pathname);
  const agencies = assignedAgencies(session?.user);
  const activeAgency = agencies.find(({ id }) => id === agencyId);
  const alternativeAgencies = agencies.filter(({ id }) => id !== agencyId);
  const publisherId = publisherIdFromPath(pathname);
  const activePublisher = publishersQuery.data?.content.find(({ id }) => id === publisherId);
  const accountName = accountDisplayName({ user: session?.user, activeAgency, activePublisher });
  const publishers = canOpenPublishers ? (publishersQuery.data?.content ?? []) : [];
  const alternativePublishers = publishers.filter(({ id }) => id !== publisherId);
  const showMainDashboard = canOpenMain && (publisherId !== null || agencyId !== null);
  const showDashboardSwitcher =
    !isSuperAdmin &&
    (showMainDashboard || alternativePublishers.length > 0 || alternativeAgencies.length > 0);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          aria-label={dashboardT('account.menu')}
          className="mx-auto h-10 w-10 justify-center rounded-full p-0"
          variant="ghost"
        >
          <span className="bg-muted flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold">
            {accountInitials(accountName)}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 max-w-[calc(100vw-2rem)] p-2">
        <DropdownMenuLabel className="truncate px-2 py-2" title={accountName}>
          {accountName}
        </DropdownMenuLabel>
        {showDashboardSwitcher ? (
          <ShigoAccountDashboardSwitcher
            showMainDashboard={showMainDashboard}
            publishers={alternativePublishers}
            agencies={alternativeAgencies}
            publisherLandingPath={publisherLandingPath}
          />
        ) : null}
        <AccountAppearanceItems themeMode={themeMode} onThemeModeChange={setThemeMode} />
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onSelect={() => {
            void navigate({ to: '/logout' });
          }}
        >
          <LogOut className="h-4 w-4" />
          <span>{dashboardT('auth.logout')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Theme choice inside the account menu; on wider screens the header toggle owns it. */
function AccountAppearanceItems({
  themeMode,
  onThemeModeChange,
}: {
  themeMode: ThemeMode;
  onThemeModeChange: (mode: ThemeMode) => void;
}) {
  return (
    <div className="sm:hidden" data-testid="mobile-account-appearance">
      <DropdownMenuSeparator />
      <DropdownMenuLabel className="text-muted-foreground text-xs">
        {dashboardT('account.appearance')}
      </DropdownMenuLabel>
      <DropdownMenuRadioGroup
        value={themeMode}
        onValueChange={(value) => {
          const mode = Object.values(ThemeMode).find((option: string) => option === value);
          if (mode) onThemeModeChange(mode);
        }}
      >
        <DropdownMenuRadioItem value={ThemeMode.Light}>
          {dashboardT('theme.light')}
        </DropdownMenuRadioItem>
        <DropdownMenuRadioItem value={ThemeMode.Dark}>
          {dashboardT('theme.dark')}
        </DropdownMenuRadioItem>
        <DropdownMenuRadioItem value={ThemeMode.System}>
          {dashboardT('theme.system')}
        </DropdownMenuRadioItem>
      </DropdownMenuRadioGroup>
    </div>
  );
}
